import React, { useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { Tab, Tabs } from '@mui/material';

import OlcButton from '~/components/General/OlcButton';
import OlcDataGrid from '~/components/General/OlcDataGrid';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import { dateFormat } from '~/utils/date-format';

import { DASHBOARD_PAGE_SIZE } from '../../constants';
import useDashboardPagination from '../../hooks/useDashboardPagination';
import {
  filterActiveOrders,
  formatCurrency,
  formatNumber,
  getStatusTone,
  humanize,
} from '../../utils';

const OrdersTable = ({ dashboardWidgets, isLoading }) => {
  const navigate = useNavigate();
  const [ordersView, setOrdersView] = useState('active');
  const activeOrders = useMemo(
    () =>
      filterActiveOrders(dashboardWidgets?.activeOrders).map((order) => ({
        ...order,
        mailingDate: order.mailingDate
          ? dateFormat(order.mailingDate, true, true)
          : 'Not yet mailed',
      })),
    [dashboardWidgets?.activeOrders]
  );
  const futureOrders = useMemo(
    () =>
      (dashboardWidgets?.futureOrders || []).map((order) => ({
        ...order,
        mailingDate: order.mailingDate
          ? dateFormat(order.mailingDate, true, true)
          : 'Not yet scheduled',
      })),
    [dashboardWidgets?.futureOrders]
  );
  const selectedOrders = ordersView === 'future' ? futureOrders : activeOrders;
  const pagination = useDashboardPagination(selectedOrders, DASHBOARD_PAGE_SIZE);

  const orderColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Order',
        minWidth: 220,
        flex: 1.6,
        sortable: false,
        compactValueGetter: (row) =>
          `${row.name || humanize(row.productType) || `Order ${row.id}`} ID ${row.id}`,
        renderCell: ({ row }) => (
          <div className="dashboard-primary-cell">
            <strong>{row.name || humanize(row.productType) || `Order ${row.id}`}</strong>
            <small>ID: {row.id}</small>
          </div>
        ),
      },
      {
        field: 'mailingDate',
        headerName: 'Mailing date',
        minWidth: 150,
        flex: 1,
        sortable: false,
        compactValueGetter: (row) => row.mailingDate || row.scheduledDate || 'Not yet mailed',
        renderCell: ({ row }) => row.mailingDate || row.scheduledDate || 'Not yet mailed',
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 130,
        flex: 0.8,
        sortable: false,
        renderCell: ({ row }) => (
          <span className={`status-badge ${getStatusTone(row.status)}`}>
            {humanize(row.status)}
          </span>
        ),
      },
      {
        field: 'quantity',
        headerName: 'Quantity',
        align: 'right',
        headerAlign: 'right',
        minWidth: 110,
        flex: 0.7,
        sortable: false,
        compactValueGetter: (row) => formatNumber(row.quantity),
        renderCell: ({ row }) => formatNumber(row.quantity),
      },
      {
        field: 'cost',
        headerName: 'Spend',
        align: 'right',
        headerAlign: 'right',
        minWidth: 110,
        flex: 0.7,
        sortable: false,
        compactValueGetter: (row) => formatCurrency(row.cost),
        renderCell: ({ row }) => formatCurrency(row.cost),
      },
    ],
    []
  );

  return (
    <section className="dashboard-table-section">
      <div className="section-heading">
        <div>
          <div className="heading-row">
            <Tabs
              value={ordersView}
              onChange={(_, nextView) => {
                setOrdersView(nextView);
                pagination.resetPage();
              }}
              className="dashboard-text-tabs dashboard-order-tabs"
              aria-label="Order timeframe"
              TabIndicatorProps={{ className: 'dashboard-tab-indicator' }}
            >
              <Tab
                value="active"
                label="Active orders"
                className="dashboard-text-tab"
                disableRipple
              />
              <Tab
                value="future"
                label="Future orders"
                className="dashboard-text-tab"
                disableRipple
              />
            </Tabs>
            <span className="time-pill">All time</span>
          </div>
          <p className="dashboard-order-description">
            {ordersView === 'active'
              ? "What's currently in production, in transit, or recently delivered."
              : 'Orders scheduled to mail in the future.'}
          </p>
        </div>
        <div className="section-actions">
          <OlcButton
            type="button"
            variant="secondary"
            size="small"
            onClick={() => navigate('/orders')}
          >
            View all orders
          </OlcButton>
        </div>
      </div>

      <div className="dashboard-grid-shell">
        <OlcDataGrid
          rows={pagination.visibleRows}
          columns={orderColumns}
          loading={isLoading}
          hideFooter
          rowSelection={false}
          disableRowSelectionOnClick
          disableColumnMenu
          rowHeight={61}
          columnHeaderHeight={48}
          getRowId={(row) => row.id}
          onRowClick={({ id }) => navigate(`/orders/detail/overview/${id}`)}
          slots={{
            noRowsOverlay: () => (
              <div className="dashboard-grid-empty">
                {ordersView === 'active'
                  ? 'No active orders right now.'
                  : 'No future orders scheduled.'}
              </div>
            ),
          }}
        />
        {(isLoading || selectedOrders.length > 0) && (
          <OlcTablePagination
            count={pagination.pageCount}
            page={pagination.page}
            pageSize={pagination.pageSize}
            onPageChange={(_, nextPage) => pagination.setPage(nextPage)}
            from={pagination.from}
            to={pagination.to}
            total={pagination.total}
            loading={isLoading}
            showPageSize={false}
          />
        )}
      </div>
    </section>
  );
};

export default OrdersTable;
